import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Building2, MapPin, FileText, Gavel, MessageSquare, Heart,
  ArrowRight,
} from 'lucide-react';

const services = [
  {
    icon: Building2,
    title: 'Hukum Perusahaan',
    desc: 'Pendirian, restrukturisasi, akuisisi, dan kepatuhan korporasi untuk bisnis Anda.',
    color: 'from-blue-500/20 to-blue-600/5',
    border: 'hover:border-blue-500/30',
  },
  {
    icon: MapPin,
    title: 'Sengketa Tanah & Properti',
    desc: 'Penyelesaian sengketa lahan, pengurusan sertifikat, dan hak atas properti.',
    color: 'from-gold-500/20 to-gold-600/5',
    border: 'hover:border-gold-500/30',
  },
  {
    icon: FileText,
    title: 'Perjanjian & Kontrak',
    desc: 'Penyusunan, review, dan negosiasi kontrak bisnis yang kuat dan terlindungi.',
    color: 'from-emerald-500/20 to-emerald-600/5',
    border: 'hover:border-emerald-500/30',
  },
  {
    icon: Gavel,
    title: 'Litigasi & Pengadilan',
    desc: 'Representasi profesional di pengadilan dengan strategi hukum yang solid.',
    color: 'from-red-500/20 to-red-600/5',
    border: 'hover:border-red-500/30',
  },
  {
    icon: MessageSquare,
    title: 'Konsultasi Hukum',
    desc: 'Sesi konsultasi mendalam dengan advokat berpengalaman untuk berbagai permasalahan.',
    color: 'from-purple-500/20 to-purple-600/5',
    border: 'hover:border-purple-500/30',
  },
  {
    icon: Heart,
    title: 'Hukum Keluarga',
    desc: 'Perceraian, hak asuh, warisan, dan permasalahan hukum keluarga lainnya.',
    color: 'from-pink-500/20 to-pink-600/5',
    border: 'hover:border-pink-500/30',
  },
];

const cardVariants = {
  hidden:  { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Services() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section id="layanan" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-600/30 to-transparent" />
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage: `radial-gradient(ellipse 70% 50% at 30% 50%, rgba(14, 22, 48, 0.8) 0%, transparent 70%)`,
        }}
      />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-gold-400 text-sm font-semibold tracking-widest uppercase mb-3">
            Layanan Hukum
          </span>
          <h2 className="section-title mb-4">
            Solusi Lengkap untuk{' '}
            <span className="gold-text">Kebutuhan Hukum</span>{' '}
            Anda
          </h2>
          <div className="divider-gold mx-auto mb-5" />
          <p className="section-subtitle max-w-2xl mx-auto">
            Kami menyediakan berbagai layanan hukum komprehensif yang dirancang untuk
            melindungi kepentingan Anda dan memastikan kepatuhan hukum di setiap aspek.
          </p>
        </motion.div>

        {/* Cards grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((svc, i) => (
            <motion.div
              key={svc.title}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className={`group relative glass-light rounded-2xl p-7 border border-white/5 ${svc.border} transition-all duration-400 cursor-pointer overflow-hidden`}
            >
              {/* Gradient background on hover */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${svc.color} opacity-0 group-hover:opacity-100 transition-opacity duration-400 rounded-2xl`}
              />

              <div className="relative z-10">
                {/* Icon */}
                <div className="w-14 h-14 rounded-2xl gold-gradient flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <svc.icon size={26} className="text-navy-950" strokeWidth={1.8} />
                </div>

                <h3 className="font-serif text-xl font-bold text-white mb-3 group-hover:gold-text transition-all">
                  {svc.title}
                </h3>
                <p className="text-navy-300 text-sm leading-relaxed mb-5">{svc.desc}</p>

                <div className="flex items-center gap-1.5 text-gold-400 text-sm font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-2 group-hover:translate-x-0">
                  Selengkapnya <ArrowRight size={15} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
